import React from "react";
import useSeo from "../hooks/useSeo";
import { Link } from "react-router-dom";
import { Trash2, Mail, ShieldCheck, AlertCircle } from "lucide-react";

export default function DataDeletionPage() {
  useSeo({
    title: "Anmod om sletning af persondata · EMA Kurer ApS",
    description:
      "Sådan anmoder du EMA Kurer ApS om sletning af din konto og dine persondata. GDPR-konform proces, behandlet inden 30 dage.",
    path: "/data-sletning",
  });

  return (
    <div data-testid="data-deletion-page">
      {/* Hero */}
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red flex items-center gap-2">
            <ShieldCheck size={14} /> GDPR · Artikel 17 (Retten til at blive glemt)
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3">
            Anmod om sletning af dine data
          </h1>
          <p className="text-white/70 mt-3 max-w-2xl">
            Som bruger af EMA Kurer (kunde- eller chauffør-app) har du ret til at få slettet din konto og alle tilhørende
            persondata. Vi behandler din anmodning inden 30 dage og bekræfter på email.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 space-y-12">

          {/* Step 1 */}
          <div data-testid="deletion-step-1">
            <div className="flex items-start gap-4">
              <div className="bg-brand-red text-white w-10 h-10 flex items-center justify-center font-bold font-display text-lg flex-shrink-0">
                1
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-brand-dark">Send en email til os</h2>
                <p className="text-brand-text2 mt-2 leading-relaxed">
                  Send en email til{" "}
                  <a href="mailto:kontakt@emakurer.dk?subject=Anmodning%20om%20sletning%20af%20persondata"
                     className="text-brand-red font-semibold underline underline-offset-4"
                     data-testid="deletion-email-link">
                    kontakt@emakurer.dk
                  </a>{" "}
                  med emnet <strong>"Anmodning om sletning af persondata"</strong>.
                </p>
                <div className="mt-4 bg-slate-50 border-l-4 border-brand-red p-4 text-sm text-brand-text2">
                  <strong className="text-brand-dark">Inkluder i emailen:</strong>
                  <ul className="mt-2 space-y-1 list-disc pl-5">
                    <li>Den email-adresse, din konto er registreret med</li>
                    <li>Dit fulde navn (som angivet på kontoen)</li>
                    <li>Om du er kunde eller chauffør hos EMA Kurer</li>
                    <li>Eventuelle bookings-ID'er du ønsker slettet (valgfrit)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div data-testid="deletion-step-2">
            <div className="flex items-start gap-4">
              <div className="bg-brand-red text-white w-10 h-10 flex items-center justify-center font-bold font-display text-lg flex-shrink-0">
                2
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-brand-dark">Vi verificerer din identitet</h2>
                <p className="text-brand-text2 mt-2 leading-relaxed">
                  Vi sender et bekræftelseslink til din registrerede email inden for 2 hverdage for at sikre, at det er dig
                  selv der anmoder. Du klikker på linket, og din anmodning bliver gennemført.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div data-testid="deletion-step-3">
            <div className="flex items-start gap-4">
              <div className="bg-brand-red text-white w-10 h-10 flex items-center justify-center font-bold font-display text-lg flex-shrink-0">
                3
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-brand-dark">Vi sletter dine data</h2>
                <p className="text-brand-text2 mt-2 leading-relaxed">
                  Inden for <strong>30 dage</strong> sletter eller anonymiserer vi:
                </p>
                <ul className="mt-3 space-y-2 list-disc pl-5 text-brand-text2">
                  <li>Din konto (email, navn, telefonnummer, password)</li>
                  <li>Push-token og enheds-id'er</li>
                  <li>GPS-positionshistorik (for chauffører)</li>
                  <li>Profilbillede og uploadede dokumenter</li>
                  <li>Onboarding-data og certifikater (for chauffører)</li>
                  <li>Beskeder og chat-historik</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Important — data we MUST keep */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6" data-testid="deletion-legal-retention">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <h3 className="font-display font-bold text-lg text-brand-dark">Lovkrævet opbevaring</h3>
                <p className="text-brand-text2 mt-2 text-sm leading-relaxed">
                  Visse data er vi <strong>lovmæssigt forpligtet</strong> til at opbevare i en periode, selv efter du anmoder
                  om sletning:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-brand-text2 list-disc pl-5">
                  <li>
                    <strong>Bogføringsmateriale (fakturaer, betalinger):</strong> 5 år iflg. bogføringsloven §10
                  </li>
                  <li>
                    <strong>Skatte- og momsdokumentation:</strong> 5 år
                  </li>
                  <li>
                    <strong>Ansøgningsdata (chauffører):</strong> Slettes umiddelbart medmindre du beder os opbevare dem til fremtidige ansøgninger
                  </li>
                </ul>
                <p className="text-brand-text2 mt-3 text-sm">
                  Disse data anonymiseres og pseudonymiseres maksimalt, så de ikke kan kobles til din person efter den
                  lovkrævede opbevaringsperiode.
                </p>
              </div>
            </div>
          </div>

          {/* Other options */}
          <div className="bg-slate-50 p-6" data-testid="deletion-self-service">
            <h3 className="font-display font-bold text-lg text-brand-dark">Selvbetjening</h3>
            <p className="text-brand-text2 mt-2 text-sm">
              Hvis du blot vil <strong>deaktivere</strong> din konto i stedet for at slette den permanent (så du kan reaktivere
              den senere), kan du logge ind og kontakte os i appen — så pauser vi kontoen uden at slette historikken.
            </p>
          </div>

          {/* Contact box */}
          <div className="border-2 border-brand p-6 bg-white" data-testid="deletion-contact-box">
            <div className="flex items-center gap-3">
              <Mail className="text-brand-red" size={24} />
              <h3 className="font-display font-bold text-xl text-brand-dark">Klar til at indsende?</h3>
            </div>
            <p className="text-brand-text2 mt-3 text-sm">
              Klik nedenfor for at åbne en pre-udfyldt email i din mail-app.
            </p>
            <a
              href="mailto:kontakt@emakurer.dk?subject=Anmodning%20om%20sletning%20af%20persondata&body=Hej%20EMA%20Kurer%2C%0A%0AJeg%20anmoder%20hermed%20om%20sletning%20af%20mine%20persondata%20og%20konto%20jf.%20GDPR%20artikel%2017.%0A%0AKonto-email%3A%20%5Bindsæt%20din%20email%5D%0AFulde%20navn%3A%20%5Bindsæt%20dit%20navn%5D%0AKonto-type%3A%20%5Bkunde%20%2F%20chauffør%5D%0A%0AMed%20venlig%20hilsen"
              className="inline-flex items-center gap-2 mt-4 bg-brand-red text-white px-6 py-3 font-semibold hover:bg-red-700 transition"
              data-testid="deletion-mailto-cta"
            >
              <Trash2 size={16} /> Send sletningsanmodning
            </a>
          </div>

          {/* Footer links */}
          <div className="pt-8 border-t border-slate-200 flex flex-wrap gap-6 text-sm">
            <Link to="/privatlivspolitik" className="text-brand-red hover:underline">
              ← Privatlivspolitik
            </Link>
            <Link to="/handelsbetingelser" className="text-brand-red hover:underline">
              Handelsbetingelser
            </Link>
            <Link to="/" className="text-brand-text2 hover:text-brand-dark">
              Tilbage til forsiden
            </Link>
          </div>

        </div>
      </section>
    </div>
  );
}
